import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Paperclip, Loader2 } from "lucide-react";
import { ChatAttachment } from "@/components/ChatAttachment";
import { uploadChatFile, CHAT_MAX_BYTES, CHAT_ACCEPT } from "@/lib/chatUpload";
import { QuickReplies } from "@/components/support/QuickReplies";

export default function ChatBox({ threadType, refId, meId, heightClass = "h-72", onActivity, quickReplyName }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const [progress, setProgress] = useState(null);
  const endRef = useRef(null);
  const listRef = useRef(null);
  const lastId = msgs.length ? msgs[msgs.length - 1].id : "";
  const fileRef = useRef(null);
  const inputRef = useRef(null);

  const attach = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > CHAT_MAX_BYTES) { toast.error("حجم الملف أكبر من 25 ميجابايت"); return; }
    setProgress(0);
    try {
      await uploadChatFile(threadType, refId, file, setProgress);
      load();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "تعذّر رفع الملف");
    }
    setProgress(null);
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/messages/${threadType}/${refId}`);
      setMsgs(data);
      setErr("");
    } catch (e) {
      setErr("تعذّر تحميل المحادثة");
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [threadType, refId]);
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    if (onActivity && lastId) onActivity();
  }, [lastId]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await api.post(`/messages/${threadType}/${refId}`, { text });
      setText("");
      load();
    } catch (e) {
      toast.error("تعذّر إرسال الرسالة، حاول مرة أخرى");
    }
  };

  return (
    <div className={`flex flex-col ${heightClass} border border-border rounded-2xl overflow-hidden bg-card`} data-testid="chat-box">
      <div ref={listRef} className="flex-1 overflow-auto p-3 space-y-2 bg-muted/30 no-scrollbar">
        {err ? (
          <div className="text-center text-xs text-destructive py-8" data-testid="chat-error">{err}</div>
        ) : msgs.length === 0 ? (
          <div className="text-center text-xs text-muted-foreground py-8">ابدأ المحادثة...</div>
        ) : msgs.map((m) => {
          const mine = m.sender_id === meId;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-start" : "justify-end"}`}>
              <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-primary text-white" : "bg-card border border-border"}`}>
                {!mine && <div className="text-[10px] opacity-70 mb-0.5">{m.sender_name}</div>}
                {m.attachment && <ChatAttachment att={m.attachment} />}
                {m.text}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      {progress !== null && (
        <div className="px-3 py-1.5 text-xs flex items-center gap-2 bg-card border-t border-border" data-testid="chat-upload-progress">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> جارٍ رفع الملف... {progress}%
          <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full bg-accent transition-[width]" style={{ width: `${progress}%` }} /></div>
        </div>
      )}
      <form onSubmit={send} className="flex gap-2 p-2 border-t border-border bg-card">
        <input ref={fileRef} type="file" accept={CHAT_ACCEPT} className="hidden" onChange={attach} data-testid="chat-file-input" />
        <Button type="button" size="icon" variant="ghost" className="rounded-full shrink-0" disabled={progress !== null}
          onClick={() => fileRef.current?.click()} title="إرفاق صورة أو PDF" data-testid="chat-attach-btn">
          <Paperclip className="w-4 h-4" />
        </Button>
        {quickReplyName !== undefined && <QuickReplies customerName={quickReplyName} onPick={(t) => { setText(t); setTimeout(() => inputRef.current?.focus(), 50); }} />}
        <Input ref={inputRef} value={text} onChange={(e) => setText(e.target.value)} placeholder="اكتب رسالة..." data-testid="chat-input" />
        <Button type="submit" size="icon" className="rounded-full shrink-0" data-testid="chat-send"><Send className="w-4 h-4" /></Button>
      </form>
    </div>
  );
}
